// 앱 공용 부품 (디자인 기준 v2 · 2026-08-02)
//
// 화면마다 버튼·상단바·타일을 새로 만들던 것을 여기 모은다. 지금 코드에는 직접 만든
// 버튼이 656개, 상단바가 40곳에 흩어져 있어 같은 앱인데 화면마다 모양이 달랐다.
//
// 새 화면은 이 부품들의 조합으로만 만든다. 새 부품이 필요하면 docs/DESIGN-SYSTEM.md
// 에 먼저 넣고 여기에 추가한다.
"use client";

import Iconify from "@/components/ui/Iconify";
import { useState } from "react";
import { useRouter } from "next/navigation";

const cx = (...parts) => parts.filter(Boolean).join(" ");

/* ─────────────────────────── 버튼 3종 ─────────────────────────── */
// primary  = 행동(저장·등록·로그인). 화면에 하나가 원칙.
// secondary= 보조 행동(공유·편집)
// text     = 약한 행동(더보기·취소)
// danger   = 되돌릴 수 없는 행동(삭제). 예전엔 삭제도 핵크라 저장과 구별이 안 됐다.
const BUTTON_VARIANTS = {
  primary: "bg-action text-white hover:brightness-110",
  secondary:
    "border border-ink-25 text-ink hover:bg-ink/[0.04] dark:border-ink_on_dark-25 dark:text-ink_on_dark dark:hover:bg-white/[0.06]",
  text: "text-ink-70 hover:text-ink dark:text-ink_on_dark-70 dark:hover:text-ink_on_dark",
  danger: "border border-danger text-danger hover:bg-danger/[0.06]",
};

const BUTTON_SIZES = {
  md: "px-5 py-3 text-t-body",
  sm: "px-4 py-2 text-t-sub",
};

export const Button = ({
  variant = "primary",
  size = "md",
  className = "",
  loading = false,
  disabled = false,
  children,
  ...rest
}) => (
  <button
    type="button"
    disabled={disabled || loading}
    className={cx(
      // whitespace-nowrap 이 없으면 좁은 칸에서 글자가 한 글자씩 세로로 쌓인다
      // (웹 장소 상세의 "네이버에서 보기" 가 실제로 그렇게 깨졌다).
      "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-bold transition active:scale-[0.99] disabled:opacity-60",
      BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary,
      BUTTON_SIZES[size] || BUTTON_SIZES.md,
      variant === "text" && "px-2 py-1.5",
      className
    )}
    {...rest}
  >
    {loading && (
      <Iconify icon="ep:loading" className="!h-4 !w-4 animate-spin" />
    )}
    {children}
  </button>
);

/* ─────────────────────────── 상단바 ─────────────────────────── */
// 뒤로 + 제목(+ 오른쪽 자리). 지금 40곳이 각자 만들어 쓰고 있다.
export const TopBar = ({ title, onBack, right = null, sticky = true }) => {
  const router = useRouter();
  return (
    <div
      className={cx(
        "flex items-center gap-2 border-b border-hairline bg-surface px-4 py-3 dark:border-hairline-dark dark:bg-surface-dark",
        sticky && "sticky top-0 z-20"
      )}
    >
      <button
        type="button"
        aria-label="뒤로"
        onClick={onBack || (() => router.back())}
        className="rounded-full p-1 active:bg-ink/[0.06]"
      >
        <Iconify icon="formkit:left" className="!h-5 !w-5" />
      </button>
      <span className="min-w-0 flex-1 truncate text-t-section">{title}</span>
      {right}
    </div>
  );
};

/* ─────────────────────────── 탭 ─────────────────────────── */
// 활성 표시는 검정 밑줄. 핑크는 행동에만 쓴다(디자인 규칙 §10).
export const Tabs = ({ items = [], value, onChange, className = "" }) => (
  <div
    className={cx(
      "flex items-center gap-6 border-b border-hairline dark:border-hairline-dark",
      className
    )}
  >
    {items.map((item) => {
      const active = item.value === value;
      return (
        <button
          key={item.value}
          type="button"
          onClick={() => onChange?.(item.value)}
          className={cx(
            "relative py-2.5 text-t-body transition",
            active
              ? "font-extrabold text-ink dark:text-ink_on_dark"
              : "font-semibold text-ink-45 dark:text-ink_on_dark-45"
          )}
        >
          {item.label}
          {/* 0 은 표시하지 않는다 — 없다는 걸 숫자로 적으면 눈만 어지럽다 */}
          {item.count ? (
            <span className="ml-1 text-t-label text-ink-45 dark:text-ink_on_dark-45">
              {item.count}
            </span>
          ) : null}
          {active && (
            <span className="absolute -bottom-px left-0 right-0 h-[2px] rounded bg-ink dark:bg-ink_on_dark" />
          )}
        </button>
      );
    })}
  </div>
);

/* ─────────────────────────── 목록 타일 ─────────────────────────── */
// 장소 한 줄. 모바일 플레이스 · 웹 플레이스 · 검색 결과 · 프로필 저장한 곳이
// 모두 이 부품을 쓴다. 예전에는 네 군데가 다 달랐다 — 사진이 왼쪽/오른쪽/없음으로
// 갈리고, 같은 별점이 한쪽은 핑크 한쪽은 금색이었다.
//
// 규칙(디자인 기준 v2)
//   - 사진은 항상 왼쪽 정사각. 없으면 같은 크기 자리표시(글 시작 위치를 맞춘다)
//   - 세 줄 고정: 이름 / 업종·지역 / 메타. 메타가 없으면 그 줄만 비운다
//   - 구분자는 가운뎃점 하나
//   - 별은 금색, 출처를 함께 적는다
//   - 오른쪽 자리만 맥락에 따라 바뀐다(화살표·거리·삭제…)

// 사진이 잘 안 그려지는 문제 때문에 next/image 를 쓰지 않는다.
// 모바일 시트는 translateY 합성 레이어라, iOS 사파리가 그 안에서 로드가 끝난
// next/image 를 리플로우 전까지 안 그렸다(썸네일이 비어 보이다가 다른 화면 갔다
// 오면 나타남). 맨 <img> + 로드 직후 자기 레이어를 한 번 흔들어 강제로 그리게 한다.
// 썸네일은 세로 3:4 다. 앱의 다른 사진(플레이 피드·상세·카드)이 전부 세로라
// 목록만 정사각이면 같은 사진이 화면마다 다르게 잘린다. 가게 사진은 간판과 입구가
// 주인공이라 세로 프레임이 그걸 살린다. 64x85 는 정사각보다 줄이 21px 만 높아져
// 한 화면에 보이는 개수가 거의 그대로다(72x96 은 32px 높아져 4곳으로 준다).
const TileThumb = ({ src, alt, size = "h-[85px] w-16" }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span
        className={cx(
          size,
          "flex shrink-0 items-center justify-center rounded-card border border-hairline bg-surface-2 dark:border-hairline-dark dark:bg-surface-2dark"
        )}
      >
        <Iconify icon="mdi:image-outline" className="!h-5 !w-5 text-ink-25" />
      </span>
    );
  }

  return (
    <img
      key={src}
      src={src}
      alt={alt || ""}
      decoding="async"
      loading="eager"
      onError={() => setFailed(true)}
      onLoad={(e) => {
        const el = e.currentTarget;
        if (!el) return;
        el.style.transform = "translateZ(0)";
        window.requestAnimationFrame(() => {
          try {
            el.style.transform = "";
          } catch (_) {}
        });
      }}
      className={cx(
        size,
        "shrink-0 rounded-card border border-hairline object-cover dark:border-hairline-dark"
      )}
    />
  );
};

/* ─────────────────────────── 저장 목록 카드 ─────────────────────────── */
// 2열 격자. 칸 사이만 벌리고 바깥 여백은 화면이 정한다.
export const PlaceCardGrid = ({ children, className = "" }) => (
  // 열 수를 화면 폭이 아니라 **담는 칸 폭**에 맞춘다.
  // 처음엔 sm/lg/xl 로 나눴는데, 프로필 본문처럼 좁은 칸 안에서는 화면만 넓다고
  // 5열이 돼 카드가 잘아졌다. 한 칸을 150px 이상으로 두고 들어가는 만큼 채운다 —
  // 모바일 430px 에서는 2열, 700px 칸에서는 4열이 된다.
  <div
    className={cx(
      "grid gap-x-3 gap-y-[18px] p-3",
      "[grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]",
      className
    )}
  >
    {children}
  </div>
);

// 카드 한 장: 사진 3:4 · 이름 · "업종 · 시 구".
//
// 부제에서 주소를 시/구까지만 남기는 이유는 카드 높이를 고르게 하기 위해서다.
// 전체 주소를 넣으면 긴 것만 두 줄이 되어 격자가 어긋나 보인다. 어느 동네인지는
// 시 구 로 충분하고, 나머지는 눌러 들어가면 나온다.
//
// 사진이 없는 곳은 자리표시자를 **같은 높이로** 둔다. 그 카드만 낮추면 격자가
// 어긋나 오히려 더 눈에 걸린다.
//
// 매장 로고 자리는 아직 비워 둔다 — 매장 계정 시스템이 생겨야 로고가 들어온다.
export const PlaceCard = ({
  imageUrl = "",
  title,
  kind = "",
  address = "",
  meta = null,
  verified = false,
  corner = null,
  onClick,
  className = "",
}) => {
  const [failed, setFailed] = useState(false);
  const area = String(address || "").split(" ").slice(0, 2).join(" ");
  const sub = [kind, area].filter(Boolean).join(" · ");
  const showImage = Boolean(imageUrl) && !failed;

  return (
    <div className={cx("relative min-w-0", className)}>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full min-w-0 flex-col gap-2 text-left"
      >
        {showImage ? (
          <img
            key={imageUrl}
            src={imageUrl}
            alt={title || ""}
            decoding="async"
            loading="lazy"
            onError={() => setFailed(true)}
            className="aspect-[3/4] w-full rounded-card border border-hairline object-cover dark:border-hairline-dark"
          />
        ) : (
          <span className="flex aspect-[3/4] w-full items-center justify-center rounded-card border border-hairline bg-surface-2 dark:border-hairline-dark dark:bg-surface-2dark">
            <Iconify icon="mdi:image-outline" className="!h-7 !w-7 text-ink-25" />
          </span>
        )}
        <span className="min-w-0">
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate text-t-item">{title}</span>
            {verified && <VerifiedStoreBadge size={15} />}
          </span>
          {sub && (
            <span className="mt-0.5 block truncate text-t-sub text-ink-70 dark:text-ink_on_dark-70">
              {sub}
            </span>
          )}
          {meta && (
            <span className="mt-0.5 block truncate text-t-meta text-ink-70 dark:text-ink_on_dark-70">
              {meta}
            </span>
          )}
        </span>
      </button>
      {corner && <span className="absolute right-1.5 top-1.5">{corner}</span>}
    </div>
  );
};

// 별점 한 조각. 별은 금색 하나로 고정하고 출처를 함께 적는다.
export const TileRating = ({ rating, source = "", reviews = 0 }) => {
  const value = Number(rating) || 0;
  if (value <= 0) return null;
  return (
    <span className="inline-flex items-center gap-1">
      <Iconify icon="material-symbols:star" className="!h-[15px] !w-[15px] text-star" />
      {value.toFixed(1)}
      {source && <span className="text-ink-45 dark:text-ink_on_dark-45">{source}</span>}
      {reviews > 0 && (
        <span className="text-ink-45 dark:text-ink_on_dark-45">· 리뷰 {reviews}</span>
      )}
    </span>
  );
};

export const ListTile = ({
  imageUrl = "",
  title,
  subtitle = "",
  meta = null,
  right = null,
  onClick,
  active = false,
  thumbSize,
  // 사장님이 직접 운영하는 계정이면 상호 옆에 인증 배지가 붙는다.
  verified = false,
  className = "",
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      "flex w-full items-center gap-3 border-b border-hairline px-1 py-3 text-left transition dark:border-hairline-dark",
      active
        ? "bg-ink/[0.05] dark:bg-white/[0.07]"
        : "hover:bg-ink/[0.02] dark:hover:bg-white/[0.04]",
      className
    )}
  >
    <TileThumb src={imageUrl} alt={title} size={thumbSize} />
    <span className="min-w-0 flex-1">
      <span className="flex min-w-0 items-center gap-1">
        <span className="truncate text-t-item">{title}</span>
        {verified && <VerifiedStoreBadge size={15} />}
      </span>
      {subtitle && (
        <span className="mt-0.5 block truncate text-t-sub text-ink-70 dark:text-ink_on_dark-70">
          {subtitle}
        </span>
      )}
      {meta && (
        <span className="mt-0.5 block truncate text-t-meta text-ink-70 dark:text-ink_on_dark-70">
          {meta}
        </span>
      )}
    </span>
    {right}
  </button>
);

/* ─────────────────────────── 입력칸 ─────────────────────────── */
// 오류는 제출한 뒤에만 넘긴다. 비었다고 미리 빨갛게 띄우지 않는다.
export const Field = ({
  label,
  required = false,
  error = "",
  hint = "",
  children,
  className = "",
}) => (
  <label className={cx("flex w-full flex-col", className)}>
    {label && (
      <span className="mb-1.5 text-t-sub font-bold text-ink dark:text-ink_on_dark">
        {label}
        {required && <span className="ml-0.5 text-action">*</span>}
      </span>
    )}
    {hint && (
      <span className="mb-1.5 text-t-meta text-ink-45 dark:text-ink_on_dark-45">
        {hint}
      </span>
    )}
    {children}
    {error ? (
      <span className="mt-1 text-t-sub font-semibold text-danger">{error}</span>
    ) : null}
  </label>
);

export const TextInput = ({ className = "", ...rest }) => (
  <input
    className={cx(
      "w-full rounded-chip border border-ink-25 bg-transparent px-3 py-2.5 text-t-body text-ink outline-none transition",
      "focus:border-ink dark:border-ink_on_dark-25 dark:text-ink_on_dark dark:focus:border-ink_on_dark",
      className
    )}
    {...rest}
  />
);

/* ─────────────────── 사장님 계정 인증 배지 ─────────────────── */
// 상호 옆에 붙는다. 선 결의 둥근 집 + 체크 — 집이 '가게', 체크가 '인증'.
// 핫플 로고와 같은 선 결이라 우리 것으로 읽히고, 꽉 찬 면을 쓰는 다른 앱과 갈린다.
// 켜지는 조건은 place.storeAccountVerifiedAt (관리자가 신청을 승인하면 찍힌다).
export const VerifiedStoreBadge = ({ size = 16, className = "" }) => (
  <span
    role="img"
    aria-label="사장님이 직접 운영하는 계정"
    title="사장님이 직접 운영하는 계정입니다"
    className={cx("inline-flex shrink-0 items-center text-verified", className)}
  >
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path
        d="M12 2.9c1 0 1.7.4 2.3 1l5.4 4.9c.9.8 1.3 1.8 1.1 3l-1.3 6.8c-.3 1.6-1.4 2.4-3 2.4H7.5c-1.6 0-2.7-.8-3-2.4L3.2 11.8c-.2-1.2.2-2.2 1.1-3l5.4-4.9c.6-.6 1.3-1 2.3-1z"
        strokeWidth="2.4"
      />
      <path d="M8.6 12.6l2.3 2.3 4.4-4.6" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  </span>
);

/* ─────────────────────────── 빈 상태 ─────────────────────────── */
// 비어 있는 건 고장이 아니다. 무슨 상태인지 말하고 다음 행동을 준다.
export const Empty = ({ message, hint = "", action = null, className = "" }) => (
  <div
    className={cx(
      "flex w-full flex-col items-center gap-1 px-8 py-16 text-center",
      className
    )}
  >
    <span className="text-t-body font-bold text-ink dark:text-ink_on_dark">
      {message}
    </span>
    {hint && (
      <span className="text-t-sub text-ink-45 dark:text-ink_on_dark-45">{hint}</span>
    )}
    {action && <div className="mt-3">{action}</div>}
  </div>
);
